import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { FALLBACK_META, fetchMeta, type Meta } from '../api/meta';

interface MetaState {
  meta: Meta;
  loaded: boolean;
  error: string | null;
  /** Reads /api/meta again: the admin page calls it after changing the sign-in doors. */
  reload: () => Promise<void>;
}

const MetaContext = createContext<MetaState>({
  meta: FALLBACK_META,
  loaded: false,
  error: null,
  reload: async () => {},
});

export function MetaProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<Omit<MetaState, 'reload'>>({
    meta: FALLBACK_META,
    loaded: false,
    error: null,
  });

  const reload = useCallback(async () => {
    try {
      const meta = await fetchMeta();
      document.title = meta.name;
      // The name under the icon an iPhone puts on its home screen (index.html).
      document
        .querySelector('meta[name="apple-mobile-web-app-title"]')
        ?.setAttribute('content', meta.name);
      setState({ meta, loaded: true, error: null });
    } catch (err: unknown) {
      setState((s) => ({
        meta: s.loaded ? s.meta : FALLBACK_META,
        loaded: true,
        error: String(err),
      }));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return <MetaContext.Provider value={{ ...state, reload }}>{children}</MetaContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useMeta(): MetaState {
  return useContext(MetaContext);
}
